import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Card, Form, Row, Col, Button, Table, Alert, Badge } from 'react-bootstrap';
import { getProjectByIdAction, updateProjectAction } from '../../store/reducers/project';
import { accountAuthInfoPagingAction } from '../../store/reducers/account';
import Select from 'react-select';
import DatePicker from '@app/components/CustomDatePicker';
import moment from 'moment';
import { toast } from 'react-toastify';
import { Get } from '../../utils/httpProvider';
import { ANNUAL_JOB_API } from '../../utils/apiUrl';

const ProjectEdit = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const history = useHistory();
    const { listAccount } = useSelector((state) => state.account);
    const { item } = useSelector((state) => state.project);

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [status, setStatus] = useState(0);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [actualEndDate, setActualEndDate] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [catalogJobs, setCatalogJobs] = useState([]);
    const [selectedAnnualJobId, setSelectedAnnualJobId] = useState('');
    // Trọng số nhóm công việc đã được snapshot lúc lưu (bảo vệ giá trị lịch sử)
    const [savedJobGroupWeight, setSavedJobGroupWeight] = useState(0);
    // { [userId]: percent }
    const [memberPercents, setMemberPercents] = useState({});

    useEffect(() => {
        dispatch(accountAuthInfoPagingAction({ pageIndex: 1, pageSize: 1000 }));
        dispatch(getProjectByIdAction(id));
        Get(ANNUAL_JOB_API.GET_ALL_ACTIVE, null, null)
            .then((res) => { if (res) setCatalogJobs(res); })
            .catch((err) => console.error('Lỗi tải danh mục công việc', err));
    }, [dispatch, id]);

    // Populate form from loaded item
    useEffect(() => {
        if (item && String(item.id || '') === String(id)) {
            setName(item.name || '');
            setDescription(item.description || '');
            setStatus(item.status || 0);
            setStartDate(item.startDate ? item.startDate.split('T')[0] : '');
            setEndDate(item.endDate ? item.endDate.split('T')[0] : '');
            setActualEndDate(item.actualEndDate ? item.actualEndDate.split('T')[0] : '');
            setSelectedAnnualJobId(item.annualJobId || '');

            // Đọc trọng số đã lưu snapshot - KHÔNG dùng weight hiện tại của nhóm
            // Mục đích: nếu sau này admin đổi trọng số nhóm, dự án cũ không bị ảnh hưởng
            const snapshotWeight = item.jobGroupWeight ?? 0;
            setSavedJobGroupWeight(snapshotWeight);

            if (item.members && Array.isArray(item.members)) {
                const percents = {};
                item.members.forEach(m => {
                    const uid = m.userId || m.UserId || m.id;
                    if (uid) {
                        const mWeight = m.memberWeight ?? m.MemberWeight ?? 0;
                        percents[uid] = snapshotWeight > 0 ? (mWeight / snapshotWeight) * 100 : 0;
                    }
                });
                setMemberPercents(percents);
            } else {
                setMemberPercents({});
            }
        }
    }, [item, id]);

    // jobGroupWeight dùng để validate và phân chia:
    // - Ưu tiên savedJobGroupWeight (snapshot từ DB) nếu chưa đổi nhóm
    // - Khi user chọn nhóm mới → dùng weight mới (handleJobGroupChange cập nhật savedJobGroupWeight)
    const jobGroupWeight = savedJobGroupWeight;

    const memberOptions = listAccount
        .filter(item => {
            const acc = item.account || item.Account || item.accountResponse || item.AccountResponse;
            const roles = item.roles || item.Roles || acc?.roles || acc?.Roles || [];
            return roles.some(r => (r.name || r.Name) === 'APP_KPI_C9');
        })
        .map(item => {
            const acc = item.account || item.Account || item.accountResponse || item.AccountResponse;
            const uid = item.accountId || item.AccountId || item.id || item.Id || acc?.id || acc?.Id;
            const username = item.username || item.Username || acc?.username || acc?.Username;
            const authInfo = item.accountAuthInfo || item.AccountAuthInfo || item;
            const lastName = authInfo.lastName || '';
            const middleName = authInfo.middleName || '';
            const firstName = authInfo.firstName || '';
            const fullName = `${lastName} ${middleName} ${firstName}`.trim().replace(/\s+/g, ' ');
            return { value: uid, label: fullName ? `${fullName} (${username})` : username || uid };
        });

    const selectedMemberIds = Object.keys(memberPercents);

    const handleMembersChange = useCallback((selected) => {
        const newIds = selected ? selected.map(x => x.value) : [];
        setMemberPercents(prev => {
            const next = {};
            if (newIds.length === 0) return next;
            const perPerson = newIds.length > 0 ? (100 / newIds.length) : 0;
            newIds.forEach(uid => {
                next[uid] = prev[uid] !== undefined ? prev[uid] : perPerson;
            });
            return next;
        });
    }, []);

    const handleJobChange = (selectedOption) => {
        const newJobId = selectedOption ? selectedOption.value : '';
        if (item?.annualJobId && !selectedOption) {
            // Do not allow clearing if project already has a job
            return;
        }
        
        setSelectedAnnualJobId(newJobId);
        
        if (String(newJobId) === String(item?.annualJobId)) {
            // Restore snapshot weight if changing back to the original job
            const originalWeight = item?.jobGroupWeight ?? 0;
            setSavedJobGroupWeight(originalWeight);
            // Restore exact previous member percents
            if (item?.members && Array.isArray(item.members)) {
                const percents = {};
                item.members.forEach(m => {
                    const uid = m.userId || m.UserId || m.id;
                    if (uid) {
                        const mWeight = m.memberWeight ?? m.MemberWeight ?? 0;
                        percents[uid] = originalWeight > 0 ? (mWeight / originalWeight) * 100 : 0;
                    }
                });
                setMemberPercents(percents);
            }
        } else {
            const job = catalogJobs.find(j => String(j.id) === String(newJobId));
            const newWeight = job ? Number(job.jobGroupWeight || 0) : 0;
            // Cập nhật snapshot weight theo nhóm mới được chọn
            setSavedJobGroupWeight(newWeight);
            // Reset phân chia đều theo nhóm mới
            setMemberPercents(prev => {
                const ids = Object.keys(prev);
                if (ids.length === 0) return prev;
                const perPerson = ids.length > 0 ? (100 / ids.length) : 0;
                const next = {};
                ids.forEach(uid => { next[uid] = perPerson; });
                return next;
            });
        }
    };

    const totalPercent = Object.values(memberPercents).reduce((s, p) => s + Number(p || 0), 0);
    const totalWeight = (jobGroupWeight * totalPercent) / 100;

    const handlePercentChange = (uid, val) => {
        setMemberPercents(prev => ({ ...prev, [uid]: val === '' ? '' : Number(val) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim()) { toast.error('Vui lòng nhập tên dự án'); return; }
        const hasInvalidPercent = Object.values(memberPercents).some(p => Number(p || 0) < 0);
        if (hasInvalidPercent) {
            toast.error('Tỉ lệ của từng thành viên không được âm');
            return;
        }
        const members = Object.entries(memberPercents).map(([uid, p]) => ({
            userId: uid,
            weight: (jobGroupWeight * Number(p || 0)) / 100
        }));

        const model = {
            id: id,
            data: {
                name: name.trim(),
                description: description.trim(),
                status: Number(status),
                startDate: startDate || null,
                endDate: endDate || null,
                actualEndDate: actualEndDate || null,
                memberIds: selectedMemberIds,
                members,
                annualJobId: selectedAnnualJobId ? Number(selectedAnnualJobId) : null
            }
        };

        try {
            setSubmitting(true);
            await dispatch(updateProjectAction(model)).unwrap();
            toast.success('Cập nhật dự án thành công');
            history.push('/project');
        } catch (err) {
            toast.error('Cập nhật dự án thất bại');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Card>
            <Card.Header as="h5">Sửa dự án</Card.Header>
            <Card.Body>
                <Form onSubmit={handleSubmit}>
                    {/* Tên dự án */}
                    <Form.Group as={Row} className="mb-3">
                        <Form.Label column sm={2}>Tên dự án <span className="text-danger">*</span></Form.Label>
                        <Col sm={10}>
                            <Form.Control
                                type="text"
                                placeholder="Nhập tên dự án"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                            />
                        </Col>
                    </Form.Group>

                    {/* Danh mục công việc */}
                    <Form.Group as={Row} className="mb-3">
                        <Form.Label column sm={2}>Danh mục công việc</Form.Label>
                        <Col sm={10}>
                            <Select
                                isClearable={!item?.annualJobId}
                                placeholder="-- Chọn danh mục công việc --"
                                value={selectedAnnualJobId ? {
                                    value: selectedAnnualJobId,
                                    label: (() => {
                                        const job = catalogJobs.find(j => String(j.id) === String(selectedAnnualJobId));
                                        if (job) return `${job.name} - Điểm: ${job.jobGroupWeight ?? 0}`;
                                        if (String(item?.annualJobId) === String(selectedAnnualJobId) && item?.annualJobName) {
                                            // Handle inactive job not in catalogJobs
                                            return `${item?.annualJobName} - Điểm: ${item?.jobGroupWeight ?? 0}`;
                                        }
                                        return '';
                                    })()
                                } : null}
                                onChange={handleJobChange}
                                options={catalogJobs.map((job) => ({
                                    value: job.id,
                                    label: `${job.name} - Điểm: ${job.jobGroupWeight ?? 0}`
                                }))}
                            />
                        </Col>
                    </Form.Group>

                    {/* Ngày */}
                    <Form.Group as={Row} className="mb-3">
                        <Form.Label column sm={2}>Ngày bắt đầu</Form.Label>
                        <Col sm={4}>
                            <DatePicker 
                                className="form-control"
                                selected={startDate ? new Date(startDate) : null}
                                onChange={(date) => setStartDate(date ? moment(date).format('YYYY-MM-DD') : '')}
                                dateFormat="dd/MM/yyyy"
                                placeholderText="dd/MM/yyyy"
                                isClearable
                            />
                        </Col>
                        <Form.Label column sm={2}>Ngày kết thúc dự kiến</Form.Label>
                        <Col sm={4}>
                            <DatePicker 
                                className="form-control"
                                selected={endDate ? new Date(endDate) : null}
                                onChange={(date) => setEndDate(date ? moment(date).format('YYYY-MM-DD') : '')}
                                dateFormat="dd/MM/yyyy"
                                placeholderText="dd/MM/yyyy"
                                isClearable
                            />
                        </Col>
                    </Form.Group>

                    {/* Mô tả */}
                    <Form.Group as={Row} className="mb-3">
                        <Form.Label column sm={2}>Mô tả</Form.Label>
                        <Col sm={10}>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </Col>
                    </Form.Group>

                    {/* Người tham gia */}
                    <Form.Group as={Row} className="mb-3">
                        <Form.Label column sm={2}>Người tham gia</Form.Label>
                        <Col sm={10}>
                            <Select
                                isMulti
                                options={memberOptions}
                                placeholder="Gõ tên để tìm kiếm và chọn thành viên..."
                                value={memberOptions.filter(opt => selectedMemberIds.includes(String(opt.value)))}
                                onChange={handleMembersChange}
                                classNamePrefix="select"
                                isSearchable={true}
                                isClearable={true}
                                noOptionsMessage={() => 'Không tìm thấy thành viên nào có quyền APP_KPI_C9'}
                            />
                        </Col>
                    </Form.Group>

                    {/* Bảng trọng số thành viên */}
                    {selectedMemberIds.length > 0 && (
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm={2}>
                                Trọng số thành viên
                                {jobGroupWeight > 0 && (
                                    <div className="mt-1">
                                        <Badge variant="info" style={{ fontSize: '0.75rem' }}>
                                            Nhóm: {jobGroupWeight}
                                        </Badge>
                                    </div>
                                )}
                            </Form.Label>
                            <Col sm={10}>
                                {!selectedAnnualJobId && (
                                    <Alert variant="warning" className="py-2 mb-2" style={{ fontSize: 13 }}>
                                        Chọn danh mục công việc để tự động tính trọng số
                                    </Alert>
                                )}
                                <Table bordered size="sm" style={{ fontSize: 13 }}>
                                    <thead className="table-light">
                                        <tr>
                                            <th>Thành viên</th>
                                            <th className="text-center" style={{ width: 100 }}>Tỉ lệ (%)</th>
                                            <th style={{ width: 180 }}>
                                                Điểm
                                                {jobGroupWeight > 0 && (
                                                    <span className="text-muted ms-1" style={{ fontSize: 11 }}>
                                                        (Tổng: {totalWeight.toFixed(2)}/{jobGroupWeight})
                                                    </span>
                                                )}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedMemberIds.map(uid => {
                                            const opt = memberOptions.find(o => String(o.value) === String(uid));
                                            return (
                                                <tr key={uid}>
                                                    <td className="align-middle">{opt?.label || uid}</td>
                                                    <td className="align-middle text-center">
                                                        <Form.Control
                                                            type="number"
                                                            min={0}
                                                            step={0.01}
                                                            value={memberPercents[uid] ?? 0}
                                                            onChange={(e) => handlePercentChange(uid, e.target.value)}
                                                            size="sm"
                                                            style={{ width: 120, margin: '0 auto' }}
                                                        />
                                                    </td>
                                                    <td className="align-middle fw-bold">
                                                        {((jobGroupWeight * (memberPercents[uid] ?? 0)) / 100).toFixed(2)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </Col>
                        </Form.Group>
                    )}

                    {/* Buttons */}
                    <Form.Group as={Row}>
                        <Col sm={{ span: 10, offset: 2 }}>
                            <div className="d-flex gap-2">
                                <Button type="submit" variant="primary" disabled={submitting}>
                                    {submitting ? 'Đang lưu...' : 'Cập nhật'}
                                </Button>
                                <Button variant="secondary" onClick={() => history.push('/project')} disabled={submitting}>
                                    Hủy
                                </Button>
                            </div>
                        </Col>
                    </Form.Group>
                </Form>
            </Card.Body>
        </Card>
    );
};

export default ProjectEdit;
